"use client";

import { ArrowRight, Calculator, Atom, Brain, Monitor, Users, Search } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const modules = [{
  icon: Calculator,
  title: "Calculus Mastery",
  description: "Deep-dive into limits, derivatives, and integrals. We break down complex theorems into intuitive, manageable steps for total conceptual clarity."
}, {
  icon: Atom,
  title: "AP Physics Prep",
  description: "Rigorous preparation for AP Physics 1, 2, and C. Master mechanics, electromagnetism, and thermodynamics with precision-engineered problem sets."
}, {
  icon: Brain,
  title: "SAT Math Strategy",
  description: "High-impact techniques for the digital SAT. We focus on pattern recognition, time-management, and eliminating common traps to maximize your score."
}, {
  icon: Monitor,
  title: "Digital Whiteboard",
  description: "Interactive, real-time collaborative sessions. Watch complex equations unfold on our high-fidelity digital canvas, designed for visual learners."
}, {
  icon: Users,
  title: "Client Success",
  description: "Proven results backed by data. Our students consistently achieve significant score improvements through our structured, goal-oriented curriculum."
}, {
  icon: Search,
  title: "Diagnostic Testing",
  description: "Identify your baseline with our proprietary 10-question diagnostic. Receive a detailed performance analysis to tailor your learning path."
}];
function ModuleCard({
  module,
  index
}: {
  module: typeof modules[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const Icon = module.icon;
  return <div ref={ref} data-index={index} className="flex flex-col transition-all duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionDelay: `${index * 150}ms`
  }}>
      <div className="h-px w-full bg-primary/60" />
      <div className="pt-8">
        <Icon className="h-8 w-8 text-primary" strokeWidth={1.25} />
        <h3 className="font-default text-2xl text-foreground mt-6 tracking-tight">
          {module.title}
        </h3>
        <p className="font-default text-base leading-relaxed text-muted-foreground mt-4">
          {module.description}
        </p>
        <Link to="/ResourcesPage" className="inline-flex items-center gap-2 font-default text-sm tracking-wide text-primary hover:text-primary/70 transition-colors mt-8 group">
          Learn more
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
        </Link>
      </div>
    </div>;
}
export default function TheStemAdvantage() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="3282" id="the-stem-advantage" className="dark bg-background py-24 md:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div ref={headerRef} className="lg:col-span-6 transition-all duration-1000 ease-out" style={{
          opacity: headerVisible ? 1 : 0,
          transform: headerVisible ? "translateY(0)" : "translateY(20px)"
        }}>
            <p className="font-default text-xs tracking-[0.25em] uppercase text-primary mb-8">
              — Academic Precision
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[3rem] leading-[1.1] text-foreground tracking-tight">
              Engineered for success.{" "}
              <em className="italic font-light">Mastered</em> by logic.
            </h2>
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mt-8 max-w-xl">
              ApexTutor provides elite STEM and test prep mentorship. We combine high-level engineering principles with personalized instruction to rebuild your academic confidence and maximize your potential.
            </p>
          </div>

          <div ref={imageRef} className="lg:col-span-6 transition-all duration-1000 ease-out" style={{
          opacity: imageVisible ? 1 : 0,
          transform: imageVisible ? "translateY(0)" : "translateY(20px)",
          transitionDelay: "200ms"
        }}>
            <div className="aspect-square w-full overflow-hidden">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/digital_whiteboard_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/digital_whiteboard_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700728/img/digital_whiteboard_session.png-wvc-sizes" alt="Focused student working on complex equations on a digital whiteboard" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>

        
        <div className="mt-24 md:mt-28 lg:mt-[100px] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 md:gap-x-[60px] gap-y-16 md:gap-y-20 lg:gap-y-[80px]">
          {modules.map((module, i) => <ModuleCard key={i} module={module} index={i} />)}
        </div>
      </div>
    </section>;
}